/**
 * Destructive controls, kept in one place so they are hard to reach by accident.
 *
 * See .docs/settings-danger-zone.md.
 *
 * @module
 */
import { Button } from "../ui/button";
import { AlertTriangleIcon, LoaderIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";

export function DangerZoneSection(props: {
  readonly changedSettingLabels: ReadonlyArray<string>;
  readonly isHardResetting: boolean;
  readonly onRestoreDefaults: () => void;
  readonly onHardReset: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-destructive-border bg-destructive-surface">
      <div className="flex items-center gap-2 border-b border-destructive-border bg-destructive-surface px-4 py-2.5">
        <AlertTriangleIcon className="size-3.5 text-destructive" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-destructive">
          Danger Zone
        </span>
      </div>

      <div className="divide-y divide-destructive/15">
        <div className="flex items-center justify-between gap-4 px-4 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-foreground">Restore default settings</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Reset all preferences back to their factory defaults.
              {props.changedSettingLabels.length > 0 ? (
                <span className="ml-1 text-warning-foreground">
                  ({props.changedSettingLabels.length} change
                  {props.changedSettingLabels.length !== 1 ? "s" : ""} detected)
                </span>
              ) : null}
            </p>
          </div>
          <Button
            size="xs"
            variant="outline"
            className="shrink-0 border-destructive-border text-destructive hover:bg-destructive-surface hover:text-destructive"
            disabled={props.changedSettingLabels.length === 0}
            onClick={props.onRestoreDefaults}
          >
            <RotateCcwIcon className="size-3.5" />
            Restore defaults
          </Button>
        </div>

        <div className="flex items-center justify-between gap-4 px-4 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-foreground">Hard reset desktop app</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Delete all local projects and threads, clear all settings, and remove saved
              environments. Cannot be undone.
            </p>
          </div>
          <Button
            size="xs"
            variant="destructive"
            className="shrink-0"
            disabled={props.isHardResetting}
            onClick={props.onHardReset}
          >
            {props.isHardResetting ? (
              <LoaderIcon className="size-3.5 animate-spin" />
            ) : (
              <Trash2Icon className="size-3.5" />
            )}
            {props.isHardResetting ? "Resetting…" : "Hard reset"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Card 1359 — resolve the connected personal-codex identity from either the
// local CLI (execution-plane surfaces) or the CMMD user store (cloud surfaces).
// Extracted from the view model to keep that function's branching in check.
